/**
 * Self-serve seat purchase, and self-serve plan and seat change (ENG-2298 /
 * ENG-2385 / ENG-2718).
 *
 * Each sellable product is a row of three cards — None, Basic, Plus — so the
 * owner sets a plan per product (a tier is how much compute a seat includes),
 * and a seat count **per product** under it (ENG-2718, phase D of
 * `docs/designs/platform/billing-seats.md` §Per-product seats): one tier per
 * product, at least one seat per product chosen, and the counts need not
 * agree across products.
 *
 * **One page, two verbs**, chosen by whether the organization already pays:
 *
 * - No live subscription → `POST /billing/checkout`, which mints a Stripe
 *   Checkout session and navigates off-origin to it. Stripe's own page shows
 *   the amount charged and is the one to trust, so the totals here come from
 *   `@/config/billingTiers`, a display copy of the Stripe prices.
 * - A live subscription → `POST /billing/subscription`, which modifies the
 *   items in place and returns JSON. No redirect, so the result is reported
 *   here. The rows preselect what the organization holds — plan and seat
 *   count, from `grants[]` and `seats.by_product` — and the figure shown is
 *   the backend's own preview rather than a monthly total this page computes.
 *
 * **Up is charged today; down waits for the renewal.** Hours are a monthly
 * pool, so the money side is made to match: a product added, moved to a
 * dearer plan, or given more seats, is charged the full month's difference
 * today and the full month's hours arrive with it; a product dropped, moved
 * to a cheaper plan, or given fewer seats, takes effect at the next renewal,
 * with nothing charged or credited and the hours kept until then. Fewer
 * seats than members hold in that product is refused (409) until they are
 * unassigned, and this page says so from `seats.by_product` before asking.
 * The preview therefore answers two things, and the
 * page shows whichever apply: `amount_due`, what the card is charged today,
 * and `scheduled`, the set the subscription moves to at `period_end`. The
 * submit says which it is about to do — pay, schedule, or both.
 *
 * **No commitment without a figure.** The change is submittable only once the
 * preview has answered for the set on screen — not while it is being worked
 * out, and not when it was refused. This is a money surface, and a plan that
 * doubles in cost must not be committed by somebody who has never been shown
 * a number; a refusal is not an exception, because the preview and the change
 * go through the same planner under the same staleness guard and refuse on
 * the same terms, so a preview that was refused is a change that would be.
 * Moving the selection asks again.
 *
 * **A scheduled change is shown, not owned, here.** `pending_change` on the
 * subscription is rendered above the form so the owner knows what is already
 * booked; cancelling it is the account panel's action. An upward change on
 * top of a schedule is refused by the backend (409), and this page says to
 * cancel the schedule first rather than repeating the wire's sentence.
 */

import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CheckoutProduct, ProductSeatsResponse } from '@/api/generated';
import {
  handleChangeSubscriptionBillingSubscriptionPostMutation,
  handleCreateCheckoutSessionBillingCheckoutPostMutation,
  handleGetCurrentOrganizationOrganizationGetOptions,
  handleGetSubscriptionSubscriptionGetOptions,
} from '@/api/generated/@tanstack/react-query.gen';
import { handlePreviewSubscriptionChangeBillingSubscriptionPreviewPost } from '@/api/generated/sdk.gen';
import { PendingChangeNote } from '@/components/billing/PendingChangeNote';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldTitle,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { formatDate, formatMoney, formatQuantity } from '@/utils/usageFormat';
import {
  type BillingTierKey,
  BILLING_TIERS,
  billingTier,
  OVERAGE_USD_PER_HOUR,
} from '@/config/billingTiers';
import {
  type BillingProductKey,
  billingProductLabel,
  SELLABLE_PRODUCTS,
} from '@/config/usageProducts';
import {
  type BillingVerb,
  billingVerb,
  dunningState,
  heldPlan as heldPlanOf,
  heldPlanItems,
  invalidateAfterSubscriptionChange,
  isBillingCheckoutEnabled,
  planPhrase,
  PREVIEW_QUERY_PREFIX,
} from '@/lib/billingCheckout';
import { apiErrorMessage } from '@/utils/apiErrorMessage';

/**
 * The ceiling the backend enforces (`billing_max_seats`), mirrored for the
 * input's own bound. Duplicated deliberately and harmlessly: the server
 * refuses regardless, so a drift here costs a round trip, not a wrong sale.
 */
const MAX_SEATS = 100;

/**
 * How long a selection has to hold still before its price is asked for.
 *
 * The preview is a real Stripe invoice preview, so it is not free to ask and
 * must not fire once per card click while somebody works down the three rows,
 * or once per keystroke while they type a seat count.
 */
const PREVIEW_DEBOUNCE_MS = 400;

/**
 * What a product row can be set to: one of the tiers, or left out. `none` is
 * a card like the others so the row reads as a plan choice, and it is the
 * plan an organization with no subscription is on — the one marked current.
 */
type Choice = BillingTierKey | 'none';

/**
 * One line of the desired set, as the change route takes it. `seats` is
 * `null` only for a held product with unbounded capacity, which has no count
 * to send and keeps what it has; the checkout route never sees a `null`
 * because a buyer's every product carries a count.
 */
interface PlanItem {
  product: BillingProductKey;
  tier: BillingTierKey;
  seats: number | null;
}

/**
 * A desired set as the one string that identifies it: the cache key of its
 * preview, and — parsed back — the request body. One serialisation for both,
 * so the cache entry and the request can never describe different sets.
 */
const selectionKey = (items: readonly CheckoutProduct[]): string =>
  JSON.stringify(items);
const selectionItems = (key: string): PlanItem[] => JSON.parse(key);

/** The per-product description keys, the only translated half. */
const PRODUCT_BODY_KEY = {
  diagnosis: 'productDiagnosisBody',
  meshgen: 'productMeshgenBody',
  simops: 'productSimopsBody',
} as const satisfies Record<string, string>;

/**
 * Every sentence that differs between the two verbs, in one table.
 *
 * "Am I changing or buying" is one question, and it was being asked again at
 * each string it picks. Both columns are complete: a page that offers the
 * change verb says nothing about buying seats, including in its refusals —
 * telling an admin who followed a link to change the plan that they may not
 * *buy seats* refuses something they never attempted.
 */
const COPY = {
  buy: {
    title: 'checkout.title',
    subtitle: 'checkout.subtitle',
    note: 'checkout.priceNote',
    submit: 'checkout.submit',
    submitting: 'checkout.submitting',
    failed: 'checkout.failed',
    ownerOnly: 'checkout.ownerOnly',
    unavailable: 'checkout.unavailable',
  },
  change: {
    title: 'checkout.changeTitle',
    subtitle: 'checkout.changeSubtitle',
    note: 'checkout.changeNote',
    submit: 'checkout.changeSubmit',
    submitting: 'checkout.changeSubmitting',
    failed: 'checkout.changeFailed',
    ownerOnly: 'checkout.changeOwnerOnly',
    unavailable: 'checkout.changeUnavailable',
  },
} as const satisfies Record<Exclude<BillingVerb, 'unknown'>, object>;

/**
 * Why a change cannot be priced — `null` when it can. The first four are the
 * backend's own 400s, said before the round trip; the last is its 409 for a
 * product given fewer seats than members hold in it, said from
 * `seats.by_product` and carrying the way out (unassign on the team page).
 */
type NoPrice =
  | {
      key:
        | 'checkout.changePickAPlan'
        | 'checkout.changeNothingYet'
        | 'checkout.changeKeepOne'
        | 'checkout.changeSeatsInvalid';
    }
  | { key: 'checkout.seatsBelowAssigned'; lines: BelowAssignedLine[] }
  | null;

/** What the preview can say, once there is a change worth pricing. */
type PreviewState =
  | { kind: 'pending' }
  | { kind: 'failed'; error: unknown }
  | {
      kind: 'ready';
      /** Charged today, for the upward part; `0` when nothing moves up. */
      amountDueCents: number;
      currency: string;
      periodEnd: string | null;
      /** The whole set held from `periodEnd` when something moves down; `[]` otherwise. */
      scheduled: CheckoutProduct[];
    };

/**
 * What one desired set costs today and what it books for the renewal, debounced.
 *
 * A **query**, not a mutation, although the route is a POST: it writes
 * nothing, and the answer belongs to the selection that asked for it. That is
 * what keeps a slow reply from overwriting a newer one — a mutation hook keeps
 * whichever call settles last, and on a figure denominated in money that is a
 * wrong price on screen, not a stale list. For the same reason a selection
 * whose price has not been asked for yet reports `pending` rather than the
 * previous selection's answer: during the debounce there IS a cached figure,
 * and it is the price of something the owner is no longer choosing.
 *
 * `null` in means there is nothing worth pricing — no change, or a change the
 * backend would refuse — and `null` out says so.
 */
function usePlanChangePreview(key: string | null): PreviewState | null {
  const [debouncedKey, setDebouncedKey] = useState<string | null>(null);
  // Trailing debounce, and it settles: the guard is what stops the state it
  // writes from restarting the timer that wrote it. Keyed on the serialised
  // selection alone, so a render that changes nothing — a focus refetch, a
  // slower preview landing — cannot re-arm the timer on the way past.
  useEffect(() => {
    if (key === debouncedKey) return;
    const timer = window.setTimeout(
      () => setDebouncedKey(key),
      PREVIEW_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [key, debouncedKey]);

  // Nothing worth pricing stops the query in the very render that says so,
  // rather than one debounce later. A committed change empties this cache
  // namespace, and an observer left enabled on the set that was just committed
  // refetches the moment its entry is dropped — into the backend's "already
  // holds exactly these plans", printed under the success banner.
  const pricing = key === null ? null : debouncedKey;

  const query = useQuery({
    queryKey: [...PREVIEW_QUERY_PREFIX, pricing],
    // A price for a selection, keyed by that selection: re-picking a set the
    // owner already saw costs nothing, and the cache is emptied outright when
    // the subscription it was priced against moves.
    staleTime: Infinity,
    // The refusals are the change route's own (403, 409, 400) and none of
    // them is retryable.
    retry: false,
    queryFn:
      pricing === null
        ? // `skipToken` rather than `enabled`, because it is also what tells
          // the type checker there is no selection to send here.
          skipToken
        : async ({ signal }) => {
            // Forwarding `signal` is what lets TanStack abort this: it only
            // cancels a query whose `queryFn` consumed the signal, and
            // otherwise lets the request run to completion. The debounce
            // collapses a burst of clicks but cannot touch a request already
            // in flight, and this one is a Stripe round trip — without the
            // signal, working down the rows leaves a trail of previews still
            // open for selections the owner has already left.
            const { data } =
              await handlePreviewSubscriptionChangeBillingSubscriptionPreviewPost(
                {
                  body: { products: selectionItems(pricing) },
                  signal,
                  throwOnError: true,
                },
              );
            return data;
          },
  });

  if (key === null) return null;
  if (pricing !== key || query.isPending) return { kind: 'pending' };
  // Carried, not flattened: the backend's refusals name the fix, and the
  // sentence this page could write instead would have to guess which refusal
  // it was.
  if (query.isError) return { kind: 'failed', error: query.error };
  return {
    kind: 'ready',
    amountDueCents: query.data.amount_due,
    currency: query.data.currency,
    periodEnd: query.data.period_end,
    scheduled: query.data.scheduled,
  };
}

type Ready = Extract<PreviewState, { kind: 'ready' }>;

/**
 * The figure charged today, in the currency the backend states.
 *
 * The backend names the currency to qualify the figure, so it is what renders
 * the sign. Every price is minted in dollars today; a euro amount under a
 * dollar sign at the moment of commitment is what reading it off the wire
 * prevents.
 */
const chargedToday = (preview: Ready, locale: string) =>
  formatMoney(preview.amountDueCents, locale, preview.currency);

/** One product the change route refused to shrink below its assigned count. */
interface BelowAssignedLine {
  product: string;
  assigned: number;
  requested: number;
}

/**
 * The change route's structured 409 for fewer seats than members hold,
 * `{"error": "seats_below_assigned", "products": [{product, assigned,
 * requested}], "message"}` — read from the fields, never the sentence. The
 * preview refuses on the same terms, so both surfaces read it. `null` for
 * any other failure, and for a body that names the code but no products,
 * which falls back to its message like every other refusal.
 */
function belowAssignedLines(error: unknown): BelowAssignedLine[] | null {
  const body = error as { error?: unknown; products?: unknown } | null;
  if (body?.error !== 'seats_below_assigned') return null;
  if (!Array.isArray(body.products)) return null;
  const lines = body.products.filter(
    (line): line is BelowAssignedLine =>
      typeof line?.product === 'string' &&
      typeof line.assigned === 'number' &&
      typeof line.requested === 'number',
  );
  return lines.length === 0 ? null : lines;
}

/**
 * Fewer seats than members hold, product by product, with the way out: the
 * seats have to come off members on the team page before the count can come
 * down. Rendered for the page's own check and for the backend's refusal.
 */
function BelowAssignedNote({
  lines,
  role,
  className,
}: {
  lines: readonly BelowAssignedLine[];
  role?: 'alert';
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <div role={role} className={className}>
      <ul className="space-y-1">
        {lines.map((line) => (
          <li key={line.product}>
            {t('checkout.seatsBelowAssignedLine', {
              product: billingProductLabel(line.product),
              assigned: line.assigned,
              requested: line.requested,
            })}
          </li>
        ))}
      </ul>
      <p>
        {t('checkout.seatsBelowAssigned')}{' '}
        <Link to="/account" className="font-medium hover:underline">
          {t('checkout.manageSeats')}
        </Link>
      </p>
    </div>
  );
}

/**
 * The refusal, as the sentence the owner can act on.
 *
 * Two refusals have a fix this page can name, and both are recognised by
 * **status**, never by matching the wire's sentence: a 402 is the card on
 * file declining the part charged today — nothing changed, and the form is
 * still the way to try again once the card is fixed — and a 409 while the
 * subscription already carries a scheduled change is the backend refusing to
 * stack an upward change on top of it. Every other refusal names its own fix
 * (which tier has no price, that the subscription moved) and is shown as the
 * backend wrote it; the fixed sentence is the fallback for a failure with
 * nothing to say, which is what `apiErrorMessage` returns `''` for.
 *
 * A 409 with no schedule on the subscription is not the pending case — the
 * subscription moved under the change, and the wire's sentence says to
 * reload. The subscription read is what says whether a schedule exists, so
 * this reads it rather than the message.
 */
function refusalMessage(
  error: unknown,
  hasPendingChange: boolean,
  fallbackKey: 'checkout.changeFailed' | 'checkout.previewFailed',
  t: TFunction,
): string {
  const status = (error as { status?: unknown } | null)?.status;
  if (status === 402) return t('checkout.cardDeclined');
  if (status === 409 && hasPendingChange) {
    return t('checkout.cancelScheduledFirst');
  }
  return apiErrorMessage(error) || t(fallbackKey);
}

/**
 * A change-path refusal, from the preview or the change: the structured 409
 * for fewer seats than members hold is read first, by its fields — it is a
 * 409 too, and would otherwise be told to cancel a scheduled change — and
 * everything else is `refusalMessage`'s sentence.
 */
function Refusal({
  error,
  hasPendingChange,
  fallbackKey,
  role,
  className,
}: {
  error: unknown;
  hasPendingChange: boolean;
  fallbackKey: 'checkout.changeFailed' | 'checkout.previewFailed';
  role?: 'alert';
  className: string;
}) {
  const { t } = useTranslation();
  const lines = belowAssignedLines(error);
  if (lines !== null) {
    return (
      <BelowAssignedNote lines={lines} role={role} className={className} />
    );
  }
  return (
    <p role={role} className={className}>
      {refusalMessage(error, hasPendingChange, fallbackKey, t)}
    </p>
  );
}

/**
 * What the change does, in the sentences that apply: what is charged today,
 * and what the subscription moves to at the renewal. Both when the change has
 * an upward and a downward part, and a third sentence when it has neither
 * with a figure — an upward change during a trial is charged nothing.
 *
 * `noPrice` is the page's own single reason there is nothing to price, so
 * this states a reason rather than re-deriving one.
 */
function ChangeOutcome({
  preview,
  noPrice,
  hasPendingChange,
}: {
  preview: PreviewState | null;
  noPrice: NoPrice;
  hasPendingChange: boolean;
}) {
  const { t, i18n } = useTranslation();

  const muted = 'text-sm text-muted-foreground';
  // Why there is no figure, in the order the owner has to act on it. A
  // `noPrice` is also exactly why there is no `preview` to read — there is
  // nothing the backend would price — so it comes first; the one with a fix
  // on another page carries its lines and the way there.
  if (noPrice?.key === 'checkout.seatsBelowAssigned') {
    return <BelowAssignedNote lines={noPrice.lines} className={muted} />;
  }
  if (noPrice !== null) return <p className={muted}>{t(noPrice.key)}</p>;
  if (preview?.kind === 'failed') {
    return (
      <Refusal
        error={preview.error}
        hasPendingChange={hasPendingChange}
        fallbackKey="checkout.previewFailed"
        className={muted}
      />
    );
  }
  if (preview?.kind !== 'ready') {
    return <p className={muted}>{t('checkout.previewPending')}</p>;
  }

  const lines: string[] = [];
  if (preview.amountDueCents > 0) {
    lines.push(
      t('checkout.chargedToday', {
        amount: chargedToday(preview, i18n.language),
      }),
    );
  }
  if (preview.scheduled.length > 0) {
    const plans = preview.scheduled.map(planPhrase).join(', ');
    lines.push(
      preview.periodEnd === null
        ? t('checkout.scheduledFromUndated', { plans })
        : t('checkout.scheduledFrom', {
            plans,
            date: formatDate(preview.periodEnd, i18n.language),
          }),
    );
  }
  if (lines.length === 0) lines.push(t('checkout.nothingToday'));
  return (
    <ul className="space-y-1 text-sm text-foreground">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

/**
 * The submit says what pressing it does — pay, schedule, or both — and `null`
 * when there is nothing to promise: before the preview has answered (the
 * button is disabled then anyway), or for a change that neither charges nor
 * schedules, which keeps the plain verb.
 */
function changeSubmitLabel(
  preview: PreviewState | null,
  locale: string,
  t: TFunction,
): string | null {
  if (preview?.kind !== 'ready') return null;
  const paying = preview.amountDueCents > 0;
  const scheduling = preview.scheduled.length > 0;
  if (paying && scheduling) {
    return t('checkout.submitPayAndSchedule', {
      amount: chargedToday(preview, locale),
    });
  }
  if (paying) {
    return t('checkout.submitPayToday', {
      amount: chargedToday(preview, locale),
    });
  }
  return scheduling ? t('checkout.submitSchedule') : null;
}

/**
 * The page's frame. Separate so an unknown verb can render it holding nothing
 * — the page waits on that read rather than painting words it may have to take
 * back.
 */
function Page({ children }: { children?: ReactNode }) {
  return (
    <div className="h-full flex-1 overflow-y-auto bg-muted">
      <div className="mx-auto max-w-2xl px-8 py-16">{children}</div>
    </div>
  );
}

/** One plan card: the `FieldLabel` choice-card recipe around a radio. */
function PlanCard({
  id,
  value,
  title,
  body,
  badge,
}: {
  id: string;
  value: Choice;
  title: string;
  body: string;
  badge?: string;
}) {
  return (
    <FieldLabel htmlFor={id} className="font-normal">
      <Field orientation="horizontal">
        <FieldContent>
          <FieldTitle>
            {title}
            {badge && <Badge variant="secondary">{badge}</Badge>}
          </FieldTitle>
          <FieldDescription className="text-xs">{body}</FieldDescription>
        </FieldContent>
        {/* The card's `Field` is a `role="group"`, which axe does not read
            through when naming a control from its label, so the radio names
            itself. */}
        <RadioGroupItem value={value} id={id} aria-label={title} />
      </Field>
    </FieldLabel>
  );
}

function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  // Only the rows the owner has actually touched. Everything else falls back
  // to what the organization holds, which for a buyer here is none — so an
  // untouched form is the current plan, on both paths.
  const [picks, setPicks] = useState<
    Partial<Record<BillingProductKey, Choice>>
  >({});
  // Seat counts the owner has typed, per product, as typed — the untouched
  // rows fall back to what the organization holds, or to one seat.
  const [seatDrafts, setSeatDrafts] = useState<
    Partial<Record<BillingProductKey, string>>
  >({});

  const organizationQuery = useQuery(
    handleGetCurrentOrganizationOrganizationGetOptions(),
  );
  const subscriptionQuery = useQuery(
    handleGetSubscriptionSubscriptionGetOptions(),
  );
  const checkout = useMutation({
    ...handleCreateCheckoutSessionBillingCheckoutPostMutation(),
    onSuccess: (data) => {
      // A full navigation, not the router: the destination is Stripe's origin.
      window.location.assign(data.url);
    },
  });
  const change = useMutation({
    ...handleChangeSubscriptionBillingSubscriptionPostMutation(),
    // No redirect on this path — the route returns JSON — so the result is
    // reported in place, and the reads are re-asked rather than written from
    // the response (`invalidateAfterSubscriptionChange` says why each).
    onSuccess: () => invalidateAfterSubscriptionChange(queryClient),
  });

  const role = organizationQuery.data?.my_role;
  const isOwner = role === 'owner';
  // Buy or change — and nothing at all until the subscription read answers.
  const verb = billingVerb(
    subscriptionQuery.data?.status,
    subscriptionQuery.isSuccess,
  );
  const alreadySubscribed = verb === 'change';
  // The server's rule, read back rather than copied: one trial per
  // organization, ever, and `0` once it is spent (ENG-2400).
  const trialDays = subscriptionQuery.data?.checkout_trial_days ?? 0;
  const pendingChange = subscriptionQuery.data?.pending_change ?? null;
  // How many members hold a seat in each product (ENG-2718) — the floor a
  // reduction cannot go under. Only on the change path; a buyer holds
  // nothing. `?.` on `by_product` for a payload cached from before it.
  const seatsByProduct: Record<string, ProductSeatsResponse> = alreadySubscribed
    ? (subscriptionQuery.data?.seats.by_product ?? {})
    : {};

  // The grants only mean something once the org pays; a buyer's rows all
  // start on None, which is the plan they are on.
  const grants = alreadySubscribed
    ? (subscriptionQuery.data?.grants ?? [])
    : [];
  /**
   * The plan the organization holds for one product, as a card value. `null`
   * (see `heldPlan` in the lib) has no card to mark, so the row starts
   * unselected and the owner has to say what they want before anything can
   * be sent.
   */
  const heldPlan = (product: BillingProductKey): Choice | null =>
    heldPlanOf(grants, product);
  const choiceOf = (product: BillingProductKey): Choice | null =>
    picks[product] ?? heldPlan(product);
  // A new selection makes the last outcome stale — both the success banner
  // and the refusal describe the set that was sent, not this one. Guarded
  // because `reset()` notifies unconditionally, so calling it on a mutation
  // that has never run costs a second render per card click for nothing.
  const forgetOutcome = () => {
    if (!change.isIdle) change.reset();
  };
  const choose = (product: BillingProductKey, choice: Choice) => {
    forgetOutcome();
    setPicks((current) => ({ ...current, [product]: choice }));
  };
  const draftSeats = (product: BillingProductKey, value: string) => {
    forgetOutcome();
    setSeatDrafts((current) => ({ ...current, [product]: value }));
  };

  /**
   * A held product's bought count, off its grant — the same source
   * `heldPlanItems` compares against: a number, `null` for unbounded
   * capacity, `undefined` when the org does not hold it (or does not pay).
   */
  const heldSeats = (product: BillingProductKey): number | null | undefined =>
    grants.find((grant) => grant.product === product)?.seats;
  /**
   * What the seat control shows for a product: the owner's draft, else the
   * held count, else one — a product being added, or bought, starts at one.
   * `null` for a held product with no seat limit, which gets no control.
   */
  const seatDraftOf = (product: BillingProductKey): string | null => {
    const held = heldSeats(product);
    if (held === null) return null;
    return seatDrafts[product] ?? String(held ?? 1);
  };
  const parseSeats = (draft: string | null): number | null =>
    draft === null ? null : Number.parseInt(draft, 10);

  /** The desired set, in registry order: each product set to a tier. */
  const lineItems = SELLABLE_PRODUCTS.flatMap((product): PlanItem[] => {
    const choice = choiceOf(product.key);
    return choice === null || choice === 'none'
      ? []
      : [
          {
            product: product.key,
            tier: choice,
            seats: parseSeats(seatDraftOf(product.key)),
          },
        ];
  });
  // A row whose current plan is not one of the three cards, and which nobody
  // has resolved by picking one.
  const unresolved = SELLABLE_PRODUCTS.some(
    (product) => choiceOf(product.key) === null,
  );
  // Both sides are built in registry order, so comparing serialisations
  // compares the sets. A held set that cannot be named (`null`) compares as
  // empty: once the owner has resolved that row, whatever they picked differs
  // from a plan nobody could name.
  const desiredKey = selectionKey(lineItems);
  const changed = desiredKey !== selectionKey(heldPlanItems(grants) ?? []);

  // Every count on screen is a whole number of seats, and every count the
  // owner typed — or is buying — is within the ceiling. A held count above
  // it (a sale made by hand) is theirs already and must not hold the form;
  // a `null` is a held product with no limit, which has no count to check.
  const seatsValid = lineItems.every((item) => {
    if (item.seats === null) return true;
    const typed = !alreadySubscribed || seatDrafts[item.product] !== undefined;
    return (
      Number.isInteger(item.seats) &&
      item.seats >= 1 &&
      (!typed || item.seats <= MAX_SEATS)
    );
  });
  // Products given fewer seats than members hold in them today: the backend
  // refuses it (409) until they are unassigned, and `seats.by_product` lets
  // this page say so, and where to go, before the round trip.
  const belowAssigned = lineItems.flatMap((item): BelowAssignedLine[] => {
    const assigned = seatsByProduct[item.product]?.assigned ?? 0;
    return item.seats !== null && item.seats < assigned
      ? [{ product: item.product, assigned, requested: item.seats }]
      : [];
  });
  // The one reason a change cannot be priced or sent, in the order the owner
  // has to act on it — each is something the backend would refuse, and saying
  // so with a disabled button and a sentence beats a round trip to be told.
  // Stated once: the submit and the money line both read this, rather than
  // each deriving the same conditions and being kept in agreement by hand.
  const noPrice: NoPrice = unresolved
    ? { key: 'checkout.changePickAPlan' }
    : !seatsValid
      ? { key: 'checkout.changeSeatsInvalid' }
      : belowAssigned.length > 0
        ? { key: 'checkout.seatsBelowAssigned', lines: belowAssigned }
        : !changed
          ? { key: 'checkout.changeNothingYet' }
          : lineItems.length === 0
            ? { key: 'checkout.changeKeepOne' }
            : null;
  // Two paths, two definitions of a submittable form.
  const formValid = alreadySubscribed
    ? noPrice === null
    : lineItems.length > 0 && seatsValid;

  // The preview is only meaningful for a submittable change, and asking for
  // one the backend would refuse just trades a price for a 400. It also stops
  // the moment a change lands: the set on screen was priced against the
  // subscription that has just moved.
  const preview = usePlanChangePreview(
    alreadySubscribed && formValid && !change.isSuccess ? desiredKey : null,
  );

  const pending = alreadySubscribed ? change.isPending : checkout.isPending;
  const canSubmit =
    formValid &&
    !pending &&
    // No commitment without a figure (see the module docstring): a change
    // needs a price for THIS set, already answered. `checkout.isSuccess`
    // navigates off-origin, so only the change path can be settled here.
    !change.isSuccess &&
    (!alreadySubscribed || preview?.kind === 'ready');

  // The recurring charge Stripe will ask for: each chosen product at its tier
  // times its own seat count, and the sum. Overage is metered, so it cannot
  // be in here. The buy path only, and `null` everywhere else: on the change
  // path the backend's preview is the figure, and one computed here would be
  // plausible, wrong and unread.
  // `seats` is never `null` on the buy path — a buyer holds nothing, so
  // every chosen product carries the count from its control, and
  // `seatsValid` has checked it — which is what the checkout route takes.
  const buyItems = lineItems.map((item) => ({
    ...item,
    seats: item.seats ?? 1,
  }));
  const priceLines =
    !alreadySubscribed && formValid
      ? buyItems.map((item) => ({
          key: item.product,
          phrase: planPhrase({ product: item.product, tier: item.tier }),
          seats: item.seats,
          usd: billingTier(item.tier).usdPerSeat * item.seats,
        }))
      : null;
  const monthlyTotal =
    priceLines === null
      ? null
      : priceLines.reduce((sum, line) => sum + line.usd, 0);
  // The same sentence on every row, so written once per render.
  const tierBodies = Object.fromEntries(
    BILLING_TIERS.map((tier) => [
      tier.key,
      t('checkout.tierBody', {
        included: formatQuantity(
          tier.allowanceSeconds,
          'seconds',
          i18n.language,
        ),
        price: tier.usdPerSeat,
      }),
    ]),
  ) as Record<BillingTierKey, string>;

  // Every sentence this page's verb decides, picked once.
  const copy = COPY[alreadySubscribed ? 'change' : 'buy'];

  // Two reasons the page is not on offer, each with its own sentence and each
  // worded for the verb — refusing a *purchase* to somebody who followed a
  // link to change their plan refuses something they never attempted. They are
  // checked in order of certainty: the build switch is known before any
  // request, the role needs a round trip. An unknown role renders neither the
  // form nor a refusal — claiming "owner only" at someone who *is* the owner
  // is worse than a moment of nothing. A live subscription is no longer one of
  // them (ENG-2385): it selects the other verb rather than refusing.
  const blocked = !isBillingCheckoutEnabled()
    ? t(copy.unavailable)
    : role !== undefined && !isOwner
      ? t(copy.ownerOnly)
      : // An `unpaid` org reads as 'change', but the backend refuses the
        // plan change for it (ENG-1233); say where the way out is instead of
        // offering a form that can only fail.
        dunningState(subscriptionQuery.data?.status) === 'paused'
        ? t('checkout.changePaused')
        : null;
  // The verb decides every word on this page, so nothing paints until the
  // subscription read has said which verb it is — including the refusals,
  // which are worded from it. A read that FAILED is not an answer either, so
  // the page stays empty rather than offering to sell seats to an
  // organization that may already have them.
  const showForm = blocked === null && role !== undefined;
  const failure = alreadySubscribed ? change.error : checkout.error;

  if (verb === 'unknown') return <Page />;

  return (
    <Page>
      <div className="mb-10 text-center">
        <h1 className="text-2xl font-semibold text-foreground">
          {t(copy.title)}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">{t(copy.subtitle)}</p>
      </div>

      {blocked !== null && (
        // `role="status"`: the sentence is on screen from mount, so the
        // primitive's assertive default would interrupt a reader arriving
        // anywhere else on the page.
        <Alert role="status">
          <AlertDescription>
            <span className="text-foreground">{blocked}</span>
            <Link to="/account" className="text-xs font-medium hover:underline">
              {t('checkout.backToAccount')}
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {showForm && pendingChange !== null && subscriptionQuery.data && (
        // What is already booked for the renewal, so the rows below — which
        // preselect what is held TODAY — are read against it. Cancelling is
        // the account panel's action; here the note only informs.
        <PendingChangeNote
          pending={pendingChange}
          grants={subscriptionQuery.data.grants}
          seats={seatsByProduct}
          className="mb-4"
        />
      )}

      {showForm && (
        <Card className="rounded-lg p-6 shadow-none">
          <fieldset>
            <legend className="text-sm font-medium text-foreground">
              {t('checkout.productsLabel')}
            </legend>
            <ul className="mt-3 space-y-5">
              {SELLABLE_PRODUCTS.map((product) => {
                const held = heldPlan(product.key);
                const selection = choiceOf(product.key);
                return (
                  <li key={product.key}>
                    <FieldSet className="gap-2">
                      <FieldLegend variant="label" className="mb-0">
                        {product.label}
                      </FieldLegend>
                      <FieldDescription className="text-xs">
                        {t(
                          `checkout.${PRODUCT_BODY_KEY[product.key]}` as const,
                        )}
                      </FieldDescription>
                      {held === null && (
                        <FieldDescription className="text-xs">
                          {t('checkout.currentPlanUnknown')}
                        </FieldDescription>
                      )}
                      <RadioGroup
                        // `''` rather than `undefined` for an unresolved
                        // row: it leaves every card unchecked while keeping
                        // the group controlled.
                        value={selection ?? ''}
                        onValueChange={(value) =>
                          choose(product.key, value as Choice)
                        }
                        className="grid grid-cols-3 gap-2"
                      >
                        <PlanCard
                          id={`plan-${product.key}-none`}
                          value="none"
                          title={t('checkout.tierNone')}
                          body={t('checkout.tierNoneBody')}
                          badge={
                            held === 'none'
                              ? t('checkout.currentPlan')
                              : undefined
                          }
                        />
                        {BILLING_TIERS.map((tier) => (
                          <PlanCard
                            key={tier.key}
                            id={`plan-${product.key}-${tier.key}`}
                            value={tier.key}
                            title={tier.label}
                            body={tierBodies[tier.key]}
                            badge={
                              held === tier.key
                                ? t('checkout.currentPlan')
                                : undefined
                            }
                          />
                        ))}
                      </RadioGroup>
                      {/* The product's own seat count (ENG-2718), under its
                          plan and only once it has one: a product left out
                          has no seats to count. A held product with no seat
                          limit has no count to change, and says so. */}
                      {selection !== null &&
                        selection !== 'none' &&
                        (seatDraftOf(product.key) === null ? (
                          <p className="text-xs text-muted-foreground">
                            {t('checkout.seatsUnbounded')}
                          </p>
                        ) : (
                          <div className="flex items-center gap-3">
                            <Label
                              htmlFor={`seats-${product.key}`}
                              className="text-xs font-medium"
                            >
                              {t('checkout.seatsFor', {
                                product: product.label,
                              })}
                            </Label>
                            <Input
                              id={`seats-${product.key}`}
                              type="number"
                              min={1}
                              max={MAX_SEATS}
                              inputMode="numeric"
                              value={seatDraftOf(product.key) ?? ''}
                              onChange={(event) =>
                                draftSeats(product.key, event.target.value)
                              }
                              className="w-24"
                            />
                          </div>
                        ))}
                    </FieldSet>
                  </li>
                );
              })}
            </ul>
          </fieldset>

          <p className="mt-4 text-xs text-muted-foreground">
            {t('checkout.seatsHelp')}
          </p>

          <div className="mt-6 space-y-2">
            {alreadySubscribed
              ? // Nothing once the change has landed: the selection on
                // screen is no longer a change to price, and the success
                // banner below already says what happened.
                !change.isSuccess && (
                  <ChangeOutcome
                    preview={preview}
                    noPrice={noPrice}
                    hasPendingChange={pendingChange !== null}
                  />
                )
              : priceLines !== null &&
                monthlyTotal !== null && (
                  // One line per product — its plan, its seats, its price —
                  // and the sum, so an unequal set is read as what it is.
                  <>
                    <ul className="space-y-1 text-sm text-foreground">
                      {priceLines.map((line) => (
                        <li key={line.key}>
                          {t('checkout.lineItem', {
                            plan: line.phrase,
                            count: line.seats,
                            amount: line.usd.toLocaleString(i18n.language),
                          })}
                        </li>
                      ))}
                    </ul>
                    <p className="text-sm font-medium text-foreground">
                      {t(
                        trialDays > 0
                          ? 'checkout.trialTotal'
                          : 'checkout.total',
                        {
                          amount: monthlyTotal.toLocaleString(i18n.language),
                          days: trialDays,
                        },
                      )}
                    </p>
                  </>
                )}
            <p className="text-xs text-muted-foreground">
              {t('checkout.overageNote', { rate: OVERAGE_USD_PER_HOUR })}{' '}
              {t(copy.note)}
            </p>
          </div>

          {change.isSuccess && (
            // The change went through; the *plan* has not arrived yet,
            // because the grants behind it are the webhook's to write. A
            // downward part is booked for `scheduled_for`, and the sentence
            // says so rather than claiming the whole change has applied.
            <Alert role="status" className="mt-4">
              <AlertDescription>
                {change.data.scheduled_for === null
                  ? t('checkout.changeDone')
                  : t('checkout.changeScheduled', {
                      date: formatDate(
                        change.data.scheduled_for,
                        i18n.language,
                      ),
                    })}
              </AlertDescription>
            </Alert>
          )}

          {failure != null &&
            // The backend writes refusals that name the fix — which seat
            // range, which products are sellable, which products hold more
            // seats than asked for — and carrying that is the whole reason
            // its refusal types exist. Rendering a fixed sentence instead
            // would make that design dead on arrival; the fixed one stays as
            // the fallback for a failure with nothing to say. The change path
            // reads the structured refusal and two statuses first — see
            // `Refusal`.
            (alreadySubscribed ? (
              <Refusal
                role="alert"
                error={failure}
                hasPendingChange={pendingChange !== null}
                fallbackKey="checkout.changeFailed"
                className="mt-4 text-xs text-destructive"
              />
            ) : (
              <p role="alert" className="mt-4 text-xs text-destructive">
                {apiErrorMessage(failure) || t(copy.failed)}
              </p>
            ))}

          <Button
            type="button"
            className="mt-4 w-full"
            disabled={!canSubmit}
            onClick={() =>
              alreadySubscribed
                ? change.mutate({ body: { products: lineItems } })
                : checkout.mutate({ body: { items: buyItems } })
            }
          >
            {pending
              ? t(copy.submitting)
              : (alreadySubscribed &&
                  changeSubmitLabel(preview, i18n.language, t)) ||
                t(copy.submit)}
          </Button>
        </Card>
      )}
    </Page>
  );
}

export const Route = createFileRoute('/_app/checkout/')({
  // Warm both reads on hover (`defaultPreload: 'intent'`), the same shape
  // `/account` uses — this page is reached from a link on that panel, so the
  // hover window exists and was going unused. Started, never awaited: the
  // organization read gates the form, and awaiting it here would put the whole
  // page behind a retry chain instead of behind the form's own pending state.
  loader: ({ context: { queryClient } }) => {
    void queryClient
      .ensureQueryData(handleGetCurrentOrganizationOrganizationGetOptions())
      .catch(() => undefined);
    void queryClient
      .ensureQueryData(handleGetSubscriptionSubscriptionGetOptions())
      .catch(() => undefined);
  },
  component: CheckoutPage,
});
